import { type FC, useCallback, useMemo, useState } from 'react';

import classNames from 'classnames';
import { uniqBy } from 'lodash-es';

import {
  httpGetByObjectTypeCategories,
  httpGetByObjectTypeCategoriesByNameViews,
  type MetadataMetadataFieldSchema,
} from '@iconik-io/iconik-api/src/services/metadata';
import type { FilterValues } from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { useAppDispatch } from '../../../../store/react_redux_typed';
import type { LoadOptionsResult } from '../../../select_field/utils';
import SelectFieldWrapper, {
  type SelectFieldWrapperProps,
} from '../select/select';
import SelectFieldMultiWrapper, {
  type SelectFieldMultiWrapperProps,
} from '../select_multi/select_multi';
import { createMetadataViewOption } from './metadata_view_option';

import styles from './select_metadata_view.module.css';

type LoadedOption = {
  label: string;
  value: string;
  fields: MetadataMetadataFieldSchema[];
};

type Props = SelectFieldWrapperProps & {
  // Metadata view ids to filter out other views.
  metadataViewIds?: string[];
  objectType: string;
  category?: string;
  onLoaded?: (options: LoadedOption[]) => void;
  // TS doesn't support type narrowing through optional properties, so it's defined as required here
  // https://github.com/microsoft/TypeScript/issues/41759
  isMulti: false;
  hiddenWhenSingle?: boolean;
  filterValuesForIndicator?: FilterValues;
};

type MultiProps = SelectFieldMultiWrapperProps & {
  // Metadata view ids to filter out other views.
  metadataViewIds?: string[];
  objectType: string;
  category?: string;
  onLoaded?: (options: LoadedOption[]) => void;
  isMulti: true;
  hiddenWhenSingle?: boolean;
  filterValuesForIndicator?: FilterValues;
};

const defaultMetadataViewIds: string[] = [];

export const SelectMetadataViewFieldWrapper: FC<Props | MultiProps> = (
  props,
) => {
  const dispatch = useAppDispatch();
  const [optionsLength, setOptionsLength] = useState(0);

  const {
    isMulti,
    hiddenWhenSingle,
    category,
    className,
    placeholder,
    objectType,
    onLoaded,
    filterValuesForIndicator,
    metadataViewIds = defaultMetadataViewIds,
  } = props;

  const optionComponent = useMemo(
    () =>
      filterValuesForIndicator
        ? createMetadataViewOption(filterValuesForIndicator)
        : undefined,
    [filterValuesForIndicator],
  );

  const loadOptions = useCallback((): Promise<LoadOptionsResult> => {
    let promise;
    if (category) {
      promise = dispatch(
        httpGetByObjectTypeCategoriesByNameViews(objectType, category),
      ).then((response) => response.body.views ?? []);
    } else {
      // Without a category fetch all categories for the specific object type
      // to show all used views in the select field.
      promise = dispatch(httpGetByObjectTypeCategories(objectType))
        .then(({ body }) =>
          Promise.all(
            body.objects.map((item) =>
              dispatch(
                httpGetByObjectTypeCategoriesByNameViews(objectType, item.name),
              ),
            ),
          ),
        )
        .then((responses) =>
          uniqBy(
            responses.flatMap((response) => response.body.views ?? []),
            'id',
          ),
        );
    }
    return promise.then((views) => {
      const options = views
        .filter((view) => {
          if (metadataViewIds.length > 0 && view.id) {
            return metadataViewIds.includes(view.id);
          }
          return true;
        })
        .map((view) => ({
          label: view.name,
          value: view.id ?? '',
          fields: view.view_fields,
        }));
      onLoaded?.(options);
      setOptionsLength(options.length);
      return {
        options,
      };
    });
  }, [category, metadataViewIds, objectType, onLoaded]);

  const sharedProps = {
    className: classNames(className, styles.select_field, {
      [styles.hidden]: !isMulti && hiddenWhenSingle && optionsLength <= 1,
    }),
    preloadOptions: true,
    loadOptions,
    placeholder: placeholder ?? 'Select View',
    optionComponent,
  };

  if (props.isMulti) {
    return <SelectFieldMultiWrapper {...props} {...sharedProps} />;
  }

  return <SelectFieldWrapper {...props} {...sharedProps} />;
};

export default SelectMetadataViewFieldWrapper;
